/** @format */

import { Tag } from "antd";
import moment from "moment";
import React, { createContext, memo, useContext, useMemo } from "react";
import styled from "styled-components";
import { ReportData, ReportCellValue } from "..";
import { useSyncedScroll } from "./ReportScrollSync";

/* ------------------------------------------------------------------ */
/* Costanti                                                           */
/* ------------------------------------------------------------------ */

// Prefisso usato dal backend per le voci "di cui" (sottoinsieme delle ore già conteggiate)
const DI_CUI_PREFIX = "di_cui_";

// Simbolo del turno di riposo restituito dal backend
const REST_SYMBOL = "R";

// Righe mostrate anche quando "Nascondi righe a zero" è attivo
const ALWAYS_VISIBLE_ROWS = ["Turno Ordinario", "Straordinario"];

// Placeholder per i valori a zero: alleggerisce la lettura della griglia
const ZERO_PLACEHOLDER = "–";

// Formato delle chiavi data restituite dal backend
const DATE_FORMAT = "DD/MM/YYYY";

// moment().day(): 0 = domenica. Due lettere per distinguere martedì/mercoledì
const WEEKDAY_SHORT = ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"];

const LABEL_COL_WIDTH = 190;
const DAY_COL_WIDTH = 44;
const SUBTOTAL_COL_WIDTH = 64;
const TOTAL_COL_WIDTH = 96;

// Sotto questa larghezza l'intestazione del mese usa la forma breve ("Set 26")
const MONTH_LABEL_MIN_WIDTH = 120;

/* ------------------------------------------------------------------ */
/* Tipi                                                               */
/* ------------------------------------------------------------------ */

type CellKind = "zero" | "value" | "rest" | "error";

interface ParsedCell {
  kind: CellKind;
  display: string;
  num: number;
}

interface DayColumn {
  key: string;
  index: number;
  day: string;
  weekday: string;
  isWeekend: boolean;
  isMonthStart: boolean;
  fullDate: string;
}

interface MonthGroup {
  key: string;
  label: string;
  shortLabel: string;
  displayLabel: string;
  days: DayColumn[];
}

interface ReportRowModel {
  key: string;
  label: string;
  cells: ParsedCell[];
  subtotals: { [monthKey: string]: ParsedCell };
  total: ParsedCell;
  isEmpty: boolean;
}

interface ReportModel {
  months: MonthGroup[];
  dayCount: number;
  showSubtotals: boolean;
  mainRows: ReportRowModel[];
  detailRows: ReportRowModel[];
  anomalyDays: number;
}

/* ------------------------------------------------------------------ */
/* Parsing / formattazione valori                                     */
/* ------------------------------------------------------------------ */

const cellFormatter = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 });
const totalFormatter = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Il backend restituisce valori eterogenei:
 *  - numeri JSON (0, 8.5)
 *  - stringhe con virgola decimale ("7,5", "160,00", "1.234,50")
 *  - stringhe intere ("8")
 *  - simboli ("R" per riposo, simbolo_errore_report per le anomalie, es. "Err")
 * Restituisce null se il valore non è numerico.
 */
const parseNumber = (value: ReportCellValue | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return isFinite(value) ? value : null;

  const raw = String(value).trim();
  const normalized = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return parseFloat(normalized);
};

const parseCell = (
  value: ReportCellValue | null | undefined,
  formatter: Intl.NumberFormat,
): ParsedCell => {
  const num = parseNumber(value);

  if (num === null) {
    const raw = String(value).trim();
    return {
      kind: raw.toUpperCase() === REST_SYMBOL ? "rest" : "error",
      display: raw,
      num: 0,
    };
  }

  if (num === 0) return { kind: "zero", display: ZERO_PLACEHOLDER, num: 0 };

  return { kind: "value", display: formatter.format(num), num };
};

/**
 * Un valore è un'anomalia se non è numerico e non è il simbolo di riposo.
 * Stessa regola usata per colorare le celle: il filtro e la tabella non possono divergere.
 */
export const isAnomalyValue = (value: ReportCellValue | null | undefined): boolean => {
  if (parseNumber(value) !== null) return false;
  return String(value).trim().toUpperCase() !== REST_SYMBOL;
};

/** Numero di giorni con almeno un'anomalia per il dipendente */
export const countAnomalyDays = (dataRow: ReportData): number =>
  Object.values(dataRow.date || {}).filter(day =>
    Object.values(day || {}).some(value => isAnomalyValue(value)),
  ).length;

// Somma arrotondata ai centesimi, per evitare residui tipo 0.30000000000000004
const sumCells = (cells: ParsedCell[]): number =>
  Math.round(cells.reduce((sum, c) => sum + c.num, 0) * 100) / 100;

/* ------------------------------------------------------------------ */
/* Costruzione del modello dati                                       */
/* ------------------------------------------------------------------ */

const buildReport = (dataRow: ReportData): ReportModel => {
  const dayKeys = Object.keys(dataRow.date);

  // Raggruppo i giorni per mese (chiave YYYY-MM: gestisce anche il cambio d'anno)
  const months: MonthGroup[] = [];

  dayKeys.forEach((key, index) => {
    const m = moment(key, DATE_FORMAT);
    const monthKey = m.format("YYYY-MM");
    const lastGroup = months.length ? months[months.length - 1] : undefined;
    const isNewMonth = !lastGroup || lastGroup.key !== monthKey;

    if (isNewMonth) {
      months.push({
        key: monthKey,
        label: capitalize(m.format("MMMM YYYY")),
        shortLabel: capitalize(m.format("MMM YY")),
        displayLabel: "",
        days: [],
      });
    }

    months[months.length - 1].days.push({
      key,
      index,
      day: m.format("DD"),
      weekday: WEEKDAY_SHORT[m.day()],
      isWeekend: m.day() === 0 || m.day() === 6,
      isMonthStart: isNewMonth && index > 0,
      fullDate: capitalize(m.format("dddd D MMMM YYYY")),
    });
  });

  // I subtotali mensili servono solo se il periodo copre più di un mese
  const showSubtotals = months.length > 1;

  months.forEach(group => {
    const width = group.days.length * DAY_COL_WIDTH + (showSubtotals ? SUBTOTAL_COL_WIDTH : 0);
    group.displayLabel = width >= MONTH_LABEL_MIN_WIDTH ? group.label : group.shortLabel;
  });

  // Unione ordinata delle chiavi di tutti i giorni (non solo del primo)
  const rowKeys: string[] = [];
  dayKeys.forEach(day => {
    Object.keys(dataRow.date[day] || {}).forEach(key => {
      if (!rowKeys.includes(key)) rowKeys.push(key);
    });
  });

  const rows: ReportRowModel[] = rowKeys.map(key => {
    const cells = dayKeys.map(day => parseCell(dataRow.date[day]?.[key], cellFormatter));

    const subtotals: ReportRowModel["subtotals"] = {};
    if (showSubtotals) {
      months.forEach(group => {
        subtotals[group.key] = parseCell(
          sumCells(group.days.map(d => cells[d.index])),
          totalFormatter,
        );
      });
    }

    // Totale letto per CHIAVE (non per posizione); se assente lo calcolo lato client
    const backendTotal = dataRow.totals ? dataRow.totals[key] : undefined;
    const total =
      backendTotal !== undefined
        ? parseCell(backendTotal, totalFormatter)
        : parseCell(sumCells(cells), totalFormatter);

    return {
      key,
      label: key.startsWith(DI_CUI_PREFIX) ? key.slice(DI_CUI_PREFIX.length) : key,
      cells,
      subtotals,
      total,
      isEmpty: cells.every(c => c.kind === "zero") && total.kind === "zero",
    };
  });

  const anomalyDays = dayKeys.filter((_, i) => rows.some(r => r.cells[i].kind === "error")).length;

  return {
    months,
    dayCount: dayKeys.length,
    showSubtotals,
    mainRows: rows.filter(r => !r.key.startsWith(DI_CUI_PREFIX)),
    detailRows: rows.filter(r => r.key.startsWith(DI_CUI_PREFIX)),
    anomalyDays,
  };
};

/**
 * Ricava il periodo effettivamente restituito dal backend (primo e ultimo giorno).
 * Da usare nella pagina: <ReportPeriodInfo {...getPeriodFromReport(reportData)} />
 */
export const getPeriodFromReport = (
  reportData?: { [k: string]: ReportData } | ReportData[],
): { from: moment.Moment; to: moment.Moment } | null => {
  if (!reportData) return null;
  const first = Object.values(reportData)[0];
  if (!first || !first.date) return null;

  const keys = Object.keys(first.date);
  if (!keys.length) return null;

  return {
    from: moment(keys[0], DATE_FORMAT),
    to: moment(keys[keys.length - 1], DATE_FORMAT),
  };
};

/* ------------------------------------------------------------------ */
/* Componenti                                                         */
/* ------------------------------------------------------------------ */

/*
 * Opzioni di visualizzazione passate tramite context e NON come prop.
 * LazyList (lazylist-react) è un React.memo che si ri-renderizza solo quando cambiano
 * le key dei figli: una prop come hideEmptyRows non arriverebbe mai alle righe.
 * Il context invece raggiunge i consumer anche attraverso i memo.
 */
interface ReportViewOptions {
  hideEmptyRows: boolean;
}

const ReportViewOptionsContext = createContext<ReportViewOptions>({ hideEmptyRows: false });

export const ReportViewOptionsProvider = ({
  hideEmptyRows,
  children,
}: ReportViewOptions & { children: React.ReactNode }): JSX.Element => {
  const value = useMemo(() => ({ hideEmptyRows }), [hideEmptyRows]);
  return (
    <ReportViewOptionsContext.Provider value={value}>{children}</ReportViewOptionsContext.Provider>
  );
};

interface Props {
  dataRow: ReportData;
}

function UserRowComponent({ dataRow }: Props): JSX.Element {
  const { hideEmptyRows } = useContext(ReportViewOptionsContext);
  const report = useMemo(() => buildReport(dataRow), [dataRow]);
  const scrollRef = useSyncedScroll();
  const { months, dayCount, showSubtotals, mainRows, detailRows, anomalyDays } = report;

  const filterRows = (rows: ReportRowModel[]) =>
    hideEmptyRows ? rows.filter(r => !r.isEmpty || ALWAYS_VISIBLE_ROWS.includes(r.label)) : rows;

  const visibleMainRows = filterRows(mainRows);
  const visibleDetailRows = filterRows(detailRows);

  const subtotalCount = showSubtotals ? months.length : 0;
  const tableMinWidth =
    LABEL_COL_WIDTH +
    TOTAL_COL_WIDTH +
    dayCount * DAY_COL_WIDTH +
    subtotalCount * SUBTOTAL_COL_WIDTH;

  const { user } = dataRow;

  return (
    <UserBlock className="user-row">
      <UserHeader>
        <div>
          <UserName>
            {user.surname} {user.name}
          </UserName>
          <UserMeta>
            <span>{user.fiscal_code}</span>
            {!!dataRow.group_name && <span>{dataRow.group_name}</span>}
            <span>
              Ore da contratto: <strong>{user.contract_weekly_hours ?? ZERO_PLACEHOLDER}</strong> a
              settimana
            </span>
          </UserMeta>
        </div>
        <UserTags>
          <Tag>Singola timbratura: {user.is_single_badge ? "sì" : "no"}</Tag>
          {!!user.is_forfettizzato && <Tag color="purple">Straordinario forfettizzato</Tag>}
          {anomalyDays > 0 ? (
            <Tag color="red">
              {anomalyDays} {anomalyDays === 1 ? "giorno" : "giorni"} con anomalie
            </Tag>
          ) : (
            <Tag color="green">Nessuna anomalia</Tag>
          )}
        </UserTags>
      </UserHeader>

      <TableScroll
        ref={scrollRef}
        tabIndex={0}
        aria-label={`Presenze di ${user.surname} ${user.name}`}
      >
        <ReportTable style={{ "--table-min-width": `${tableMinWidth}px` } as React.CSSProperties}>
          <colgroup>
            <col className="col-label" />
            {months.map(group => (
              <React.Fragment key={group.key}>
                {group.days.map(d => (
                  <col key={d.key} className="col-day" />
                ))}
                {showSubtotals && <col className="col-subtotal" />}
              </React.Fragment>
            ))}
            <col className="col-total" />
          </colgroup>

          <thead>
            <tr>
              <th className="label" rowSpan={2}>
                Tipologia
              </th>
              {months.map((group, i) => (
                <th
                  key={group.key}
                  className={i > 0 ? "month month-start" : "month"}
                  colSpan={group.days.length + (showSubtotals ? 1 : 0)}
                  title={group.label}
                  data-month={group.key}
                  data-month-label={group.label}
                  data-month-short={group.shortLabel}
                >
                  <span className="month-label">{group.displayLabel}</span>
                </th>
              ))}
              <th className="total" rowSpan={2}>
                {showSubtotals ? "Totale periodo" : "Totale"}
              </th>
            </tr>
            <tr>
              {months.map(group => (
                <React.Fragment key={group.key}>
                  {group.days.map(d => (
                    <th key={d.key} className={dayClassName(d)} title={d.fullDate}>
                      <DayNumber>{d.day}</DayNumber>
                      <DayName>{d.weekday}</DayName>
                    </th>
                  ))}
                  {showSubtotals && (
                    <th className="subtotal" title={`Totale di ${group.label.toLowerCase()}`}>
                      <DayNumber>Tot.</DayNumber>
                      <DayName>{group.shortLabel}</DayName>
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {visibleMainRows.map(row => (
              <ReportRow key={row.key} row={row} months={months} showSubtotals={showSubtotals} />
            ))}
          </tbody>

          {visibleDetailRows.length > 0 && (
            <tbody>
              <tr className="section">
                <th className="label">Di cui</th>
                <td colSpan={dayCount + subtotalCount + 1}>
                  <span className="sticky-note">
                    Ore già comprese nelle voci sopra, riportate a titolo di dettaglio
                  </span>
                </td>
              </tr>
              {visibleDetailRows.map(row => (
                <ReportRow key={row.key} row={row} months={months} showSubtotals={showSubtotals} />
              ))}
            </tbody>
          )}
        </ReportTable>
      </TableScroll>
    </UserBlock>
  );
}

export const UserRow = memo(UserRowComponent);
UserRow.displayName = "UserRow";

const dayClassName = (day: DayColumn, extra?: string): string | undefined => {
  const classes: string[] = [];
  if (extra) classes.push(extra);
  if (day.isWeekend) classes.push("weekend");
  if (day.isMonthStart) classes.push("month-start");
  return classes.length ? classes.join(" ") : undefined;
};

interface ReportRowProps {
  row: ReportRowModel;
  months: MonthGroup[];
  showSubtotals: boolean;
}

function ReportRowComponent({ row, months, showSubtotals }: ReportRowProps): JSX.Element {
  return (
    <tr>
      <th scope="row" className="label" title={row.label}>
        {row.label}
      </th>
      {months.map(group => (
        <React.Fragment key={group.key}>
          {group.days.map(d => {
            const cell = row.cells[d.index];
            return (
              <td
                key={d.key}
                className={dayClassName(d, `cell-${cell.kind}`)}
                title={`${row.label}, ${d.fullDate}${
                  cell.kind === "error" ? ": anomalia da verificare" : ""
                }`}
              >
                {cell.display}
              </td>
            );
          })}
          {showSubtotals && (
            <td
              className={`subtotal cell-${row.subtotals[group.key].kind}`}
              title={`${row.label}, totale di ${group.label.toLowerCase()}`}
            >
              {row.subtotals[group.key].display}
            </td>
          )}
        </React.Fragment>
      ))}
      <td className={`total cell-${row.total.kind}`}>{row.total.display}</td>
    </tr>
  );
}

const ReportRow = memo(ReportRowComponent);
ReportRow.displayName = "ReportRow";

/* ------------------------------------------------------------------ */
/* Intestazione del report: periodo selezionato                       */
/* ------------------------------------------------------------------ */

interface ReportPeriodInfoProps {
  from: moment.Moment;
  to: moment.Moment;
  /** dipendenti presenti nel report */
  employees?: number;
  /** dipendenti visibili dopo ricerca e filtri (se diverso dal totale) */
  visibleEmployees?: number;
}

export const ReportPeriodInfo = ({
  from,
  to,
  employees,
  visibleEmployees,
}: ReportPeriodInfoProps): JSX.Element => {
  const start = from.clone().startOf("day");
  const end = to.clone().startOf("day");

  const days = end.diff(start, "days") + 1;
  const monthCount = (end.year() - start.year()) * 12 + (end.month() - start.month()) + 1;
  const crossesYear = start.year() !== end.year();
  const isFullMonth =
    start.isSame(end, "month") && start.date() === 1 && end.date() === end.daysInMonth();

  let title: string;
  if (isFullMonth) {
    title = capitalize(start.format("MMMM YYYY"));
  } else if (start.isSame(end, "month")) {
    title = `${start.format("D")} – ${end.format("D MMMM YYYY")}`;
  } else if (!crossesYear) {
    title = `${start.format("D MMMM")} – ${end.format("D MMMM YYYY")}`;
  } else {
    title = `${start.format("D MMMM YYYY")} – ${end.format("D MMMM YYYY")}`;
  }

  return (
    <PeriodBox>
      <PeriodTitle>{title}</PeriodTitle>
      <PeriodMeta>
        <span>
          Dal <strong>{start.format(DATE_FORMAT)}</strong> al{" "}
          <strong>{end.format(DATE_FORMAT)}</strong>
        </span>
        <span>
          {days} {days === 1 ? "giorno" : "giorni"}
          {monthCount > 1 ? ` su ${monthCount} mesi` : ""}
        </span>
        {employees !== undefined && (
          <span>
            {visibleEmployees !== undefined && visibleEmployees !== employees ? (
              <>
                <strong>{visibleEmployees}</strong> di {employees} dipendenti mostrati
              </>
            ) : (
              <>
                {employees} {employees === 1 ? "dipendente" : "dipendenti"}
              </>
            )}
          </span>
        )}
        {crossesYear && (
          <Tag color="orange">
            A cavallo tra il {start.year()} e il {end.year()}
          </Tag>
        )}
      </PeriodMeta>
    </PeriodBox>
  );
};

/** Legenda da mostrare una sola volta sopra l'elenco dei dipendenti */
export const ReportLegend = ({
  showMonthlyTotals = false,
}: {
  showMonthlyTotals?: boolean;
}): JSX.Element => (
  <Legend>
    <span>
      <LegendSwatch className="cell-error">Err</LegendSwatch> Anomalia da verificare
    </span>
    <span>
      <LegendSwatch className="cell-rest">R</LegendSwatch> Riposo
    </span>
    <span>
      <LegendSwatch className="cell-zero">{ZERO_PLACEHOLDER}</LegendSwatch> Nessuna ora
    </span>
    <span>
      <LegendSwatch className="weekend">&nbsp;</LegendSwatch> Sabato e domenica
    </span>
    {showMonthlyTotals && (
      <span>
        <LegendSwatch className="subtotal">Tot.</LegendSwatch> Totale del mese (anomalie escluse)
      </span>
    )}
  </Legend>
);

/* ------------------------------------------------------------------ */
/* Stili                                                              */
/* ------------------------------------------------------------------ */

const COLORS = {
  border: "#e8e8e8",
  borderLight: "#f0f0f0",
  monthBorder: "#c9cde0",
  headerBg: "#fafafa",
  monthBg: "#f3f4f9",
  weekendBg: "#f5f6fa",
  weekendHeaderBg: "#eef0f6",
  subtotalBg: "#f7f8fc",
  hoverBg: "#eef1fb",
  sectionBg: "#f3f4f9",
  text: "#262626",
  textMuted: "#8c8c8c",
  zero: "#c9c9c9",
  errorText: "#cf1322",
  errorBg: "#fff1f0",
  errorHoverBg: "#ffd8d6",
};

const UserBlock = styled.section`
  margin-bottom: 28px;
  break-inside: avoid;
  page-break-inside: avoid;
`;

const UserHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px 24px;
  margin-bottom: 10px;
`;

const UserName = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${COLORS.text};
  line-height: 1.3;
`;

const UserMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  font-size: 13px;
  color: ${COLORS.textMuted};

  strong {
    color: ${COLORS.text};
  }
`;

const UserTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  .ant-tag {
    margin-right: 0;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;

  /* barra di scorrimento sempre visibile e comoda da afferrare col mouse */
  &::-webkit-scrollbar {
    height: 12px;
  }
  &::-webkit-scrollbar-track {
    background: ${COLORS.monthBg};
  }
  &::-webkit-scrollbar-thumb {
    background: #b8bdd3;
    border: 3px solid ${COLORS.monthBg};
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #8f95b2;
  }
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-color: #b8bdd3 ${COLORS.monthBg};
  }

  /* trascinamento col mouse (classi gestite da useSyncedScroll) */
  &.can-drag tbody {
    cursor: grab;
  }
  &.is-dragging,
  &.is-dragging tbody {
    cursor: grabbing;
    user-select: none;
  }

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid #8f95b2;
    outline-offset: 2px;
  }

  @media print {
    overflow: visible;
    border-radius: 0;
  }
`;

const ReportTable = styled.table`
  width: 100%;
  min-width: var(--table-min-width);
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.text};

  col.col-label {
    width: ${LABEL_COL_WIDTH}px;
  }
  col.col-day {
    width: ${DAY_COL_WIDTH}px;
  }
  col.col-subtotal {
    width: ${SUBTOTAL_COL_WIDTH}px;
  }
  col.col-total {
    width: ${TOTAL_COL_WIDTH}px;
  }

  th,
  td {
    padding: 6px 4px;
    text-align: center;
    white-space: nowrap;
    border-bottom: 1px solid ${COLORS.borderLight};
    background: #fff;
  }

  thead th {
    background: ${COLORS.headerBg};
    border-bottom: 1px solid ${COLORS.border};
    font-weight: 600;
    line-height: 1.2;
  }

  tbody:last-child tr:last-child th,
  tbody:last-child tr:last-child td {
    border-bottom: none;
  }

  /* Colonna tipologia: fissa a sinistra durante lo scroll orizzontale */
  .label {
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
    padding-left: 12px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    border-right: 1px solid ${COLORS.border};
  }

  /* Colonna totale: fissa a destra */
  .total {
    position: sticky;
    right: 0;
    z-index: 1;
    text-align: right;
    padding-right: 12px;
    font-weight: 600;
    border-left: 1px solid ${COLORS.border};
  }

  thead .label,
  thead .total {
    z-index: 2;
  }
  thead .total {
    white-space: normal;
  }

  /* Riga dei mesi */
  thead th.month {
    background: ${COLORS.monthBg};
    text-align: left;
    padding: 7px 10px;
    font-size: 13px;
  }
  /* il nome del mese resta visibile mentre si scorre dentro il mese */
  thead th.month .month-label {
    position: sticky;
    left: ${LABEL_COL_WIDTH + 10}px;
  }

  /* Separatore verticale tra un mese e l'altro */
  .month-start {
    border-left: 2px solid ${COLORS.monthBorder};
  }

  .weekend {
    background: ${COLORS.weekendBg};
  }
  thead th.weekend {
    background: ${COLORS.weekendHeaderBg};
  }

  /* Subtotale mensile */
  .subtotal {
    background: ${COLORS.subtotalBg};
    border-left: 1px solid ${COLORS.border};
    text-align: right;
    padding-right: 8px;
  }
  thead th.subtotal {
    background: ${COLORS.weekendHeaderBg};
  }

  .cell-zero {
    color: ${COLORS.zero};
    font-weight: 400;
  }
  .cell-value {
    font-weight: 600;
  }
  .cell-rest {
    color: ${COLORS.textMuted};
    font-style: italic;
  }
  .cell-error {
    color: ${COLORS.errorText};
    background: ${COLORS.errorBg};
    font-weight: 700;
  }

  tbody tr:not(.section):hover th,
  tbody tr:not(.section):hover td {
    background: ${COLORS.hoverBg};
  }
  tbody tr:not(.section):hover td.cell-error {
    background: ${COLORS.errorHoverBg};
  }

  /* Intestazione della sezione "di cui" */
  tr.section th,
  tr.section td {
    background: ${COLORS.sectionBg};
    border-top: 2px solid ${COLORS.border};
    border-bottom: 1px solid ${COLORS.border};
    padding-top: 8px;
    padding-bottom: 8px;
  }
  tr.section th {
    font-weight: 600;
  }
  tr.section td {
    text-align: left;
    padding-left: 12px;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
  /* la nota resta visibile anche durante lo scroll orizzontale */
  tr.section .sticky-note {
    position: sticky;
    left: ${LABEL_COL_WIDTH + 12}px;
  }

  @media print {
    min-width: 0;
    font-size: 8px;

    col.col-label {
      width: 90px;
    }
    col.col-day,
    col.col-subtotal {
      width: auto;
    }
    col.col-total {
      width: 46px;
    }

    th,
    td {
      padding: 2px 1px;
    }

    .label,
    .total,
    thead th.month .month-label,
    tr.section .sticky-note {
      position: static;
    }
  }
`;

const DayNumber = styled.div`
  font-size: 13px;
`;

const DayName = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${COLORS.textMuted};
`;

const PeriodBox = styled.div`
  margin-bottom: 12px;
`;

const PeriodTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${COLORS.text};
  line-height: 1.3;
`;

const PeriodMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 20px;
  font-size: 13px;
  color: ${COLORS.textMuted};

  strong {
    color: ${COLORS.text};
    font-weight: 600;
  }

  .ant-tag {
    margin-right: 0;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 20px;
  font-size: 12px;
  color: ${COLORS.textMuted};

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const LegendSwatch = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 20px;
  padding: 0 4px;
  border: 1px solid ${COLORS.border};
  border-radius: 4px;
  background: #fff;
  font-size: 11px;

  &.cell-error {
    color: ${COLORS.errorText};
    background: ${COLORS.errorBg};
    font-weight: 700;
  }
  &.cell-rest {
    color: ${COLORS.textMuted};
    font-style: italic;
  }
  &.cell-zero {
    color: ${COLORS.zero};
  }
  &.weekend {
    background: ${COLORS.weekendBg};
  }
  &.subtotal {
    background: ${COLORS.subtotalBg};
    font-weight: 600;
  }
`;
